import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getCanonicalUrl } from "../utils/seoConfig";
import "../styles/landing.scss";

function Services() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>{t("ServicesPageTitle")}</title>
        <meta name="description" content={t("ServicesPageDescription")} />
        <meta name="keywords" content={t("ServicesPageKeywords")} />
        <link rel="canonical" href={getCanonicalUrl("/services")} />
      </Helmet>
      <div className="App">
        {/* Hero / Intro Section */}
        <section className="section services-hero">
          <div className="services-hero-content">
            <h1 className="services-hero-title">{t("ServicesHeroTitle")}</h1>
            <p className="services-hero-subtitle">
              {t("ServicesHeroSubtitle")}
            </p>
            <button
              className="primary-button services-hero-cta"
              onClick={() => {
                navigate("/");
                setTimeout(() => {
                  const contactSection = document.getElementById("contact");
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: "smooth" });
                  }
                }, 100);
              }}
            >
              {t("ServicesBookFreeConsultation")}
            </button>
          </div>
        </section>

        {/* Who We Serve / Personas Section */}
        <section className="section who-we-serve">
          <div className="who-we-serve-wrapper">
            <h2 className="section-title">{t("WhoWeServe")}</h2>
            <p className="section-subtitle">{t("WhoWeServeDesc")}</p>
            <div className="personas-grid">
              <div className="persona-item">
                <div className="persona-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 13H7V23H3V13M10 9H14V23H10V9M17 5H21V23H17V5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("OperationsManagers")}</h3>
              </div>
              <div className="persona-item">
                <div className="persona-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="18"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M8 8H16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8 12H16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8 16H12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle cx="18" cy="6" r="1.5" fill="currentColor" />
                  </svg>
                </div>
                <h3>{t("Engineers")}</h3>
              </div>
              <div className="persona-item">
                <div className="persona-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 21H21"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 21V3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 17L7 15L9 16L11 13L13 14L15 11L17 12L19 9L21 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("DataAnalysts")}</h3>
              </div>
            </div>
          </div>
        </section>

        {/* Our Offerings / Service List Section */}
        <section className="section services-offerings">
          <div className="services-offerings-wrapper">
            <h2 className="section-title">{t("OurOfferings")}</h2>
            <p className="section-subtitle">{t("OurOfferingsDesc")}</p>

            <div className="services-offerings-grid">
              {/* Service 1: Data Integration */}
              <div className="service-offering-card" data-aos="fade-up">
                <div className="service-offering-icon">
                  <svg
                    className="icon-impact"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L2 7L12 12L22 7L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 17L12 22L22 17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 12L12 17L22 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("Service1DataIntegration")}</h3>
                <p className="service-offering-description">
                  {t("Service1DataIntegrationDesc")}
                </p>
                <div className="service-problem-solution">
                  <div className="problem-solution-item">
                    <span className="problem-label">{t("Problem")}:</span>
                    <span className="problem-text">
                      {t("Service1DataIntegrationProblem")}
                    </span>
                  </div>
                  <div className="problem-solution-item">
                    <span className="solution-label">{t("Solution")}:</span>
                    <span className="solution-text">
                      {t("Service1DataIntegrationSolution")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service 2: Custom Enterprise IT Solutions */}
              <div
                className="service-offering-card"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                <div className="service-offering-icon">
                  <svg
                    className="icon-implementation"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="2"
                      y="3"
                      width="20"
                      height="18"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M8 21H16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M7 8H17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M7 12H17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M7 16H12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <h3>{t("Service2CustomEnterpriseIT")}</h3>
                <p className="service-offering-description">
                  {t("Service2CustomEnterpriseITDesc")}
                </p>
                <div className="service-problem-solution">
                  <div className="problem-solution-item">
                    <span className="problem-label">{t("Problem")}:</span>
                    <span className="problem-text">
                      {t("Service2CustomEnterpriseITProblem")}
                    </span>
                  </div>
                  <div className="problem-solution-item">
                    <span className="solution-label">{t("Solution")}:</span>
                    <span className="solution-text">
                      {t("Service2CustomEnterpriseITSolution")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service 3: Consulting */}
              <div
                className="service-offering-card"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <div className="service-offering-icon">
                  <svg
                    className="icon-project"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("Service3Consulting")}</h3>
                <p className="service-offering-description">
                  {t("Service3ConsultingDesc")}
                </p>
                <div className="service-problem-solution">
                  <div className="problem-solution-item">
                    <span className="problem-label">{t("Problem")}:</span>
                    <span className="problem-text">
                      {t("Service3ConsultingProblem")}
                    </span>
                  </div>
                  <div className="problem-solution-item">
                    <span className="solution-label">{t("Solution")}:</span>
                    <span className="solution-text">
                      {t("Service3ConsultingSolution")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service 4: Project Management and Process Improvement */}
              <div
                className="service-offering-card"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <div className="service-offering-icon">
                  <svg
                    className="icon-impact"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 11L12 14L22 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("Service4ProjectManagement")}</h3>
                <p className="service-offering-description">
                  {t("Service4ProjectManagementDesc")}
                </p>
                <div className="service-problem-solution">
                  <div className="problem-solution-item">
                    <span className="problem-label">{t("Problem")}:</span>
                    <span className="problem-text">
                      {t("Service4ProjectManagementProblem")}
                    </span>
                  </div>
                  <div className="problem-solution-item">
                    <span className="solution-label">{t("Solution")}:</span>
                    <span className="solution-text">
                      {t("Service4ProjectManagementSolution")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service 5: Business Process Automation */}
              <div
                className="service-offering-card"
                data-aos="fade-up"
                data-aos-delay="400"
              >
                <div className="service-offering-icon">
                  <svg
                    className="icon-implementation"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("Service5BusinessProcessAutomation")}</h3>
                <p className="service-offering-description">
                  {t("Service5BusinessProcessAutomationDesc")}
                </p>
                <div className="service-problem-solution">
                  <div className="problem-solution-item">
                    <span className="problem-label">{t("Problem")}:</span>
                    <span className="problem-text">
                      {t("Service5BusinessProcessAutomationProblem")}
                    </span>
                  </div>
                  <div className="problem-solution-item">
                    <span className="solution-label">{t("Solution")}:</span>
                    <span className="solution-text">
                      {t("Service5BusinessProcessAutomationSolution")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service 6: Analytics Services */}
              <div
                className="service-offering-card"
                data-aos="fade-up"
                data-aos-delay="500"
              >
                <div className="service-offering-icon">
                  <svg
                    className="icon-project"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 21H21"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 21V3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 18L7 16L9 17L11 15L13 16L15 13L17 14L19 11L21 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>{t("Service6AnalyticsServices")}</h3>
                <p className="service-offering-description">
                  {t("Service6AnalyticsServicesDesc")}
                </p>
                <div className="service-problem-solution">
                  <div className="problem-solution-item">
                    <span className="problem-label">{t("Problem")}:</span>
                    <span className="problem-text">
                      {t("Service6AnalyticsServicesProblem")}
                    </span>
                  </div>
                  <div className="problem-solution-item">
                    <span className="solution-label">{t("Solution")}:</span>
                    <span className="solution-text">
                      {t("Service6AnalyticsServicesSolution")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA / Lead Form Section */}
        <section className="section services-cta">
          <div className="services-cta-wrapper">
            <h2 className="section-title">{t("ReadyToGetStarted")}</h2>
            <p className="section-subtitle">{t("ServicesCTADescription")}</p>
            <div className="services-cta-buttons">
              <button
                className="primary-button"
                onClick={() => {
                  navigate("/");
                  setTimeout(() => {
                    const contactSection = document.getElementById("contact");
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: "smooth" });
                    }
                  }, 100);
                }}
              >
                {t("ContactUs")}
              </button>
              {/*<button
                className="secondary-button"
                onClick={() => navigate("/signup")}
              >
                {t("RequestDemo")}
              </button>*/}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Services;
